import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DS2 } from '../theme/designTokens';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { GradientText } from '../components/ui/GradientText';
import { ASSETS } from '../config/assets';
import { signInWithGoogle } from '../utils/firebase';
import { useTranslation } from '../i18n/react';

/**
 * GET STARTED — auth provider chooser (Figma "GET STARTED / PLAY ONLINE",
 * 2560×1321 export, all values ×0.75 per the established desktop scale).
 * Lives at /signup inside the DS2 shell (Sidebar on desktop, DS2 top bar
 * on mobile).
 *
 * MISSING ASSET (do not substitute): the email/sms icon for the gold
 * button — Google is wired from an approved 48×48 SVG.
 *
 * Apple/Facebook sign-up are hidden for now (product decision) — only
 * Google and email are offered. firebase.ts still exports
 * signInWithApple/signInWithFacebook for a future re-enable.
 */

// Figma 363×52 buttons ×0.75 → 272.25×39; padding 10/16, gap 12, font 22 scaled.
const AUTH_BUTTON_BASE: CSSProperties = {
  boxSizing:      'border-box',
  display:        'flex',
  flexDirection:  'row',
  alignItems:     'center',
  justifyContent: 'center',
  gap:            9,
  width:          272.25,
  height:         39,
  padding:        '7.5px 12px',
  fontFamily:     DS2.font.body,
  fontWeight:     500,
  fontSize:       16.5,
  lineHeight:     '18px',
  border:         '0.75px solid #C9A84C',
  cursor:         'pointer',
  WebkitTapHighlightColor: 'transparent',
};

function AuthIcon({ src, size }: { src: string; size: number }) {
  return (
    <img
      src={src}
      alt=""
      style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
      draggable={false}
    />
  );
}

function GhostAuthButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...AUTH_BUTTON_BASE,
        background:   '#131313',
        borderRadius: 12,
        color:        '#F5F5F5',
        cursor:       disabled ? 'not-allowed' : (onClick ? 'pointer' : 'default'),
        opacity:      disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function GoldAuthButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...AUTH_BUTTON_BASE,
        background:   DS2.gradient.gold,
        borderRadius: 11.25,
        boxShadow:    '0px 22.5px 60px -22.5px rgba(201, 168, 76, 0.45)',
        color:        '#FFFFFF',
      }}
    >
      {children}
    </button>
  );
}

export function GetStartedScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [socialLoading, setSocialLoading] = useState<'google' | null>(null);
  // Holds a translation key so a visible error re-renders on language switch.
  const [error, setError] = useState<'login.errorGeneric' | ''>('');

  async function handleSocialSignIn(provider: 'google') {
    setError('');
    setSocialLoading(provider);
    try {
      await signInWithGoogle();
      navigate('/home', { replace: true });
    } catch (err: unknown) {
      const fe = err as { code?: string; message?: string };
      console.error('[GetStartedScreen] Social sign-in error — code:', fe.code, '| message:', fe.message);
      if (fe.code !== 'auth/popup-closed-by-user' && fe.code !== 'auth/cancelled-popup-request') {
        setError('login.errorGeneric');
      }
    } finally {
      setSocialLoading(null);
    }
  }

  return (
    <div
      style={{
        position:   'relative',
        flex:       1,
        background: DS2.color.pageBg,
        display:    'flex',
        padding:    '48px 24px',
        boxSizing:  'border-box',
      }}
    >
      {/* Gradient+Blur ×2 — Figma export values ×0.75 (offsets recomputed
          against the content area, same method as the Landing glows).
          Clip layer matches the screen exactly so the glows' off-screen
          overflow can't extend the scroll area (this page must not scroll
          on desktop); the clip edge sits on the page edge, so no visible
          hard cut — unlike Landing, where sections have content below. */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <AtmosphericGlow width={1207.5} height={935.25} top={-213.75} leftOffset={-379.5} radius={0} />
        <AtmosphericGlow width={1167} height={877.5} top={273} leftOffset={308} radius={164.25} />
      </div>

      {/* Auth column — margin:auto centers it BOTH ways without the flex-center
          overflow bug (alignItems:center clips the top edge off-screen when the
          column is taller than a phone viewport). */}
      <div
        style={{
          position:      'relative',
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          maxWidth:      454,
          width:         '100%',
          margin:        'auto',
        }}
      >
        {/* Dominoes-only brand mark (Figma LOGO-WHITE crop, 246×183 landscape) */}
        <img
          src={ASSETS.brand.ICON}
          alt="The Domino Game"
          style={{ height: 137, width: 'auto', objectFit: 'contain' }}
          draggable={false}
        />

        {/* Welcome to / The Domino Game — Montserrat 700 50.688/61, ls -0.99.
            Line 2 mirrors the brand lockup: "The" and "Game" gold, "Domino" white. */}
        <h1
          style={{
            margin:        '28px 0 0',
            maxWidth:      364,
            fontFamily:    DS2.font.heading,
            fontWeight:    700,
            fontSize:      38.02,
            lineHeight:    '45.75px',
            letterSpacing: '-0.74px',
            textAlign:     'center',
            color:         '#FFFFFF',
          }}
        >
          {/* Line 1 translates; line 2 is the untranslatable brand lockup. */}
          {t('getStarted.welcome')}<br />
          <GradientText>The</GradientText> Domino <GradientText>Game</GradientText>
        </h1>

        {/* Play with friends anytime, anywhere. — Roboto 300 34.56/62 */}
        <p
          style={{
            margin:     '21px 0 0',
            fontFamily: DS2.font.body,
            fontWeight: 300,
            fontSize:   25.92,
            lineHeight: 1.8,
            textAlign:  'center',
            color:      DS2.color.textBright,
          }}
        >
          {t('getStarted.tagline')}
        </p>

        {/* Provider buttons — Google + email only for now (Apple/Facebook
            hidden, product decision). Icon heights are the Figma vector
            boxes ×0.75. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 19, marginTop: 39, alignItems: 'center' }}>
          <GhostAuthButton onClick={() => handleSocialSignIn('google')} disabled={!!socialLoading}>
            <AuthIcon src={ASSETS.auth.GOOGLE} size={11.6} />
            {socialLoading === 'google' ? t('getStarted.signingUp') : t('getStarted.withGoogle')}
          </GhostAuthButton>
          {/* Email/sms icon pending approved export — text-only until delivered */}
          <GoldAuthButton onClick={() => navigate('/signup/email')}>{t('getStarted.withEmail')}</GoldAuthButton>
        </div>

        {error && (
          <p style={{ margin: '18px 0 0', fontFamily: DS2.font.body, fontSize: 12.6, color: '#f87171', textAlign: 'center' }}>
            {t(error)}
          </p>
        )}

        {/* Log in — Roboto 700 19.2/29, underlined (Figma Vector 2) */}
        <Link
          to="/login"
          style={{
            marginTop:      41,
            fontFamily:     DS2.font.body,
            fontWeight:     700,
            fontSize:       14.4,
            lineHeight:     '21.75px',
            textAlign:      'center',
            color:          '#FFFFFF',
            textDecoration: 'underline',
          }}
        >
          {t('getStarted.logInLink')}
        </Link>
      </div>
    </div>
  );
}
